import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Car, Map, Navigation2, Sparkles, ArrowRight, CheckCircle2, X } from 'lucide-react';

const TOUR_STORAGE_KEY = 'curvio_tour_completed';

const tourSteps = [
  {
    id: 'welcome',
    title: 'Willkommen bei Curvio!',
    description: 'Deine Navigation für Oldtimer und Wohnwagen. Wir helfen dir, Routen zu finden, die für dein Fahrzeug geeignet sind - ohne steile Steigungen oder enge Straßen.',
    icon: Sparkles,
    action: null,
  },
  {
    id: 'vehicle',
    title: 'Erstelle dein Fahrzeugprofil',
    description: 'Füge dein erstes Fahrzeug hinzu, damit wir Routen berechnen können, die zu deinem Fahrzeug passen. Du kannst Abmessungen, Gewicht und maximale Steigung angeben.',
    icon: Car,
    action: 'vehicles',
  },
  {
    id: 'route',
    title: 'Plane deine erste Route',
    description: 'Gib Start und Ziel ein, und wir berechnen die beste Route für dein Fahrzeug. Du siehst Steigungen, Kurven und alternative Routen auf einen Blick.',
    icon: Navigation2,
    action: 'dashboard',
  },
  {
    id: 'complete',
    title: 'Bereit für die Fahrt!',
    description: 'Du bist startklar! Entdecke Community-Routen, speichere deine Lieblingsrouten und genieße stressfreie Fahrten mit deinem Oldtimer oder Wohnwagen.',
    icon: CheckCircle2,
    action: null,
  },
];

const QuickStartTour = ({ hasVehicles, onClose }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [skipped, setSkipped] = useState(false);

  useEffect(() => {
    // Check if tour was already completed
    const tourCompleted = localStorage.getItem(TOUR_STORAGE_KEY);
    if (!tourCompleted) {
      setIsOpen(true);
    }
  }, []);

  const handleNext = () => {
    const step = tourSteps[currentStep];
    
    // If action is required, navigate and close dialog
    if (step.action) {
      if (step.action === 'vehicles') {
        navigate('/dashboard/vehicles');
        setIsOpen(false);
        // Don't mark as complete yet - will show again
        return;
      }
      if (step.action === 'dashboard') {
        navigate('/dashboard');
        setIsOpen(false);
        localStorage.setItem(TOUR_STORAGE_KEY, 'true');
        onClose?.();
        return;
      }
    }
    
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Tour complete
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
      setIsOpen(false);
      onClose?.();
    }
  };

  const handleSkip = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setIsOpen(false);
    setSkipped(true);
    onClose?.();
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = tourSteps[currentStep];
  const progress = ((currentStep + 1) / tourSteps.length) * 100;
  const StepIcon = step.icon;

  // If user already has vehicles, skip vehicle step
  useEffect(() => {
    if (hasVehicles && currentStep === 1) {
      setCurrentStep(2);
    }
  }, [hasVehicles, currentStep]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="quickstart-dialog">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80" onClick={handleSkip} />
      
      {/* Modal Content */}
      <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-md p-6 animate-in fade-in zoom-in-95">
        {/* Close button */}
        <button 
          onClick={handleSkip}
          className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="flex items-center justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <StepIcon className="w-8 h-8 text-primary" />
          </div>
        </div>

        {/* Title & Description */}
        <h2 className="text-center text-xl font-display font-semibold text-foreground mb-2">
          {step.title}
        </h2>
        <p className="text-center text-muted-foreground mb-6">
          {step.description}
        </p>

        {/* Progress */}
        <div className="mb-4">
          <Progress value={progress} className="h-1.5" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>Schritt {currentStep + 1} von {tourSteps.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mb-6">
          {tourSteps.map((s, i) => (
            <div
              key={s.id}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentStep ? 'bg-primary' : i < currentStep ? 'bg-primary/50' : 'bg-muted'
              }`}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          {currentStep > 0 && currentStep < tourSteps.length - 1 && (
            <Button variant="ghost" onClick={handleBack} className="sm:mr-auto">
              Zurück
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
            Überspringen
          </Button>
          <Button onClick={handleNext} className="bg-primary text-primary-foreground" data-testid="quickstart-next-btn">
            {currentStep === tourSteps.length - 1 ? (
              <>Los geht's!</>
            ) : step.action ? (
              <>
                {step.action === 'vehicles' ? 'Fahrzeug erstellen' : 'Route planen'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Weiter
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Helper to reset tour (for testing)
export const resetTour = () => {
  localStorage.removeItem(TOUR_STORAGE_KEY);
};

// Helper to check if tour is completed
export const isTourCompleted = () => {
  return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
};

export default QuickStartTour;
